import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged, filter } from 'rxjs/operators';

import { TranslocoService } from '@ngneat/transloco';

import { DataList, DropDownList, DropDownListEntry, getDropdownListOptions } from '@core/interfaces';

import { endpoints } from '@environment';

import { ListOfValuesRequest } from '#types/list';

import { AuthService } from '@auth/services';

import * as ListActions from './list.actions';
import * as fromList from './list.selectors';

@Injectable({
  providedIn: 'root',
})
export class ListService {
  yesNoList$ = this.store.select(fromList.selectYesNoList);
  yesNoDropdownList$ = new BehaviorSubject<DropDownList>(null);

  translate = (key: string | string[], params?: Record<string, string>, lang?: string) =>
    this.translocoService.translate(key, params, lang);

  constructor(
    private store: Store,
    private http: HttpClient,
    private authService: AuthService,
    private translocoService: TranslocoService
  ) {
    this.translocoService
      .selectTranslation()
      .pipe(distinctUntilChanged())
      .subscribe(() => {
        const yesNo = [
          { column1: 'Y', column2: this.translate('core.common.yes') },
          { column1: 'N', column2: this.translate('core.common.no') },
        ];

        this.authService.authenticated$.pipe(filter((authenticated) => authenticated)).subscribe(() => {
          this.store.dispatch(ListActions.setStaticLists({ yesNo }));
          this.initializeDropdownLists();
        });
      });
  }

  getListOfValues<T>(listType: string, listName: string = 'all'): Observable<T> {
    return this.http.get<T>(endpoints[listType].list[listName], {
      withCredentials: true,
    });
  }

  getListOfValuesWithParameters<T>(listType: string, listName: string, parameters: ListOfValuesRequest | any): Observable<T> {
    return this.http.post<T>(endpoints[listType].list[listName], parameters, {
      withCredentials: true,
    });
  }

  getDropdownList(list: DataList, opts: getDropdownListOptions) {
    if (!list) {
      return null;
    }

    opts.includeHeader = opts.includeHeader ?? true;

    opts.header1 = opts.header1 ?? 'key';
    opts.header2 = opts.header2 ?? 'descr';
    opts.header3 = opts.header3 ?? null;
    opts.header4 = opts.header4 ?? null;
    opts.header5 = opts.header5 ?? null;
    opts.header6 = opts.header6 ?? null;
    opts.header7 = opts.header7 ?? null;
    opts.header8 = opts.header8 ?? null;
    opts.header9 = opts.header9 ?? null;
    opts.header10 = opts.header10 ?? null;

    opts.key1 = opts.key1 ?? 'key';
    opts.key2 = opts.key2 ?? 'descr';
    opts.key3 = opts.key3 ?? null;
    opts.key4 = opts.key4 ?? null;
    opts.key5 = opts.key5 ?? null;
    opts.key6 = opts.key6 ?? null;
    opts.key7 = opts.key7 ?? null;
    opts.key8 = opts.key8 ?? null;
    opts.key9 = opts.key9 ?? null;
    opts.key10 = opts.key10 ?? null;

    const values: DropDownList = [];

    if (opts.includeHeader) {
      const entry: DropDownListEntry = {
        column1: opts.header1,
        column2: opts.header2,
        disabled: true,
      };

      if (opts.header3 && opts.key3) {
        entry.column3 = opts.header3;
      }

      if (opts.header4 && opts.key4) {
        entry.column4 = opts.header4;
      }

      if (opts.header5 && opts.key5) {
        entry.column5 = opts.header5;
      }

      if (opts.header6 && opts.key6) {
        entry.column6 = opts.header6;
      }

      if (opts.header7 && opts.key7) {
        entry.column7 = opts.header7;
      }

      if (opts.header8 && opts.key8) {
        entry.column8 = opts.header8;
      }

      if (opts.header9 && opts.key9) {
        entry.column9 = opts.header9;
      }

      if (opts.header10 && opts.key10) {
        entry.column10 = opts.header10;
      }

      values.push(entry);
    }

    if (list.data) {
      for (const row of list.data) {
        const entry: DropDownListEntry = {
          column1: row[opts.key1],
          column2: row[opts.key2],
        };

        if (opts.header3 && opts.key3) {
          entry.column3 = row[opts.key3];
        }

        if (opts.header4 && opts.key4) {
          entry.column4 = row[opts.key4];
        }

        if (opts.header5 && opts.key5) {
          entry.column5 = row[opts.key5];
        }

        if (opts.header6 && opts.key6) {
          entry.column6 = row[opts.key6];
        }

        if (opts.header7 && opts.key7) {
          entry.column7 = row[opts.key7];
        }

        if (opts.header8 && opts.key8) {
          entry.column8 = row[opts.key8];
        }

        if (opts.header9 && opts.key9) {
          entry.column9 = row[opts.key9];
        }

        if (opts.header10 && opts.key10) {
          entry.column10 = row[opts.key10];
        }

        values.push(entry);
      }
    }

    return values;
  }

  initializeDropdownLists() {
    this.yesNoList$.pipe(distinctUntilChanged()).subscribe((list) => {
      this.yesNoDropdownList$.next(
        this.getDropdownList(list, {
          includeHeader: false,
          key1: 'column1',
          key2: 'column2',
          header1: this.translate('core.services.list.dropdown.header.code'),
          header2: this.translate('core.services.list.dropdown.header.description'),
        })
      );
    });
  }
}
